import { Tr, Td, Text, Box, Icon, Badge } from '@chakra-ui/react';
import { CheckIcon, CloseIcon, WarningIcon, MinusIcon } from '@chakra-ui/icons';
import { Metric, MetricGoal, User } from '../../types';
import {
  isMetricOnTrack,
  formatMetricValue,
  getOrientationSymbol,
  calculateTrailingStatus,
  WeekColumn,
} from '../../utils/scorecardCalculations';
import { ScorecardCellEditor } from './ScorecardCellEditor';

interface ScorecardRowProps {
  metric: Metric;
  goals: MetricGoal[]; // All goals for this metric
  weeks: WeekColumn[];
  users: User[];
  onUpdateGoal: (
    metricId: string,
    goalId: string | undefined,
    periodNumber: number,
    value: number | null,
    notes: string,
    dateStart: Date,
    dateEnd: Date
  ) => Promise<void>;
  onDropToIssue: (metric: Metric, week: WeekColumn, actual: number | null, goal: number) => void;
}

export function ScorecardRow({
  metric,
  goals,
  weeks,
  users,
  onUpdateGoal,
  onDropToIssue,
}: ScorecardRowProps) {
  // Helper to find goal for a specific week
  const getGoalForWeek = (weekNumber: number) => {
    return goals.find(
      (g) => g.periodType === 'week' && g.periodNumber === weekNumber
    );
  };

  let goalDisplay = '';
  let symbol = getOrientationSymbol(
    metric.goalOrientation || 'gte',
    metric.goalRangeMin,
    metric.goalRangeMax
  );

  if (metric.goalOrientation === 'range' && metric.goalRangeMin != null && metric.goalRangeMax != null) {
      const min = formatMetricValue(metric.goalRangeMin, metric.measurementType ?? 'count', '');
      const max = formatMetricValue(metric.goalRangeMax, metric.measurementType ?? 'count', metric.unit);
      goalDisplay = `${min}-${max}`;
      symbol = ''; // Clear symbol as range is self-explanatory or we can use a range icon
  } else {
    goalDisplay = formatMetricValue(
      metric.weeklyGoal || metric.quarterlyGoal || 0,
      metric.measurementType ?? 'count',
      metric.unit
    );
  }

  return (
    <Tr>
      {/* Metric Info Column - Frozen/Sticky ideally, but simple first */}
      <Td position="sticky" left={0} bg="white" zIndex={1} boxShadow="2px 0 5px -2px rgba(0,0,0,0.1)">
        <Box>
          <Text fontWeight="bold" noOfLines={1} title={metric.title || metric.metricName}>
            {metric.title || metric.metricName}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {metric.measurable}
          </Text>
        </Box>
      </Td>
      
      <Td>
        <Text fontSize="sm">
          {metric.ownerId 
            ? (users.find(u => u.uid === metric.ownerId || u.id === metric.ownerId)?.name || 
               users.find(u => u.uid === metric.ownerId || u.id === metric.ownerId)?.Name || 
               'Unknown')
            : 'Unassigned'}
        </Text> 
      </Td>

      <Td textAlign="center">
        <Text fontSize="sm" fontWeight="medium">
          {symbol} {goalDisplay}
        </Text>
      </Td>

      {/* 13 Week Columns */}
      {weeks.map((week) => {
        const goal = getGoalForWeek(week.weekNumber);
        const actualValue = goal?.actualValue ?? null;
        
        // Use weekly goal or specific goal override
        const targetGoal = goal?.goalValue ?? (metric.weeklyGoal || metric.quarterlyGoal || 0);

        const onTrack = isMetricOnTrack(
          actualValue,
          targetGoal,
          metric.goalOrientation || 'gte',
          metric.goalRangeMin,
          metric.goalRangeMax
        );

        let statusColor = 'gray.100';
        let statusIcon = null;

        if (actualValue !== null) {
          if (onTrack) {
            statusColor = 'green.50';
            statusIcon = <CheckIcon color="green.500" w={3} h={3} />;
          } else {
            statusColor = 'red.50';
            statusIcon = <CloseIcon color="red.500" w={3} h={3} />;
          }
        }

        const displayValue = formatMetricValue(
          actualValue,
          metric.measurementType ?? 'count',
          metric.unit
        );

        const isReviewWeek = week.isReviewWeek;

        return (
          <Td
            key={week.weekNumber}
            p={0}
            textAlign="center"
            bg={isReviewWeek ? 'blue.50' : 'white'}
            borderLeftWidth={isReviewWeek ? '2px' : '1px'}
            borderRightWidth={isReviewWeek ? '2px' : '1px'}
            borderColor={isReviewWeek ? 'blue.200' : 'gray.100'}
          >
            <ScorecardCellEditor
              metric={metric}
              goal={goal}
              weekLabel={`Week ${week.weekNumber}`}
              onSave={async (val, notes) => {
                await onUpdateGoal(
                  metric.id,
                  goal?.id,
                  week.weekNumber,
                  val,
                  notes,
                  week.startDate,
                  week.endDate
                );
              }}
              onDropToIssue={() => onDropToIssue(metric, week, actualValue, targetGoal)}
            >
              <Box
                h="100%"
                w="100%"
                minH="40px"
                py={2}
                cursor="pointer"
                _hover={{ bg: isReviewWeek ? 'blue.100' : 'gray.50' }}
                display="flex"
                alignItems="center" // Center vertically
                justifyContent="center" // Center horizontally
              >
                {actualValue !== null ? (
                   <Box display="flex" alignItems="center" justifyContent="center">
                    <Text 
                        fontSize="sm" 
                        fontWeight={onTrack === false ? "bold" : "normal"}
                        color={onTrack === true ? "green.600" : onTrack === false ? "red.600" : "gray.800"}
                        mr={1}
                    >
                      {displayValue}
                    </Text>
                     {/* Optional: Show small indicator if notes exist */}
                     {goal?.notes && (
                        <Box w={1.5} h={1.5} borderRadius="full" bg="blue.400" title="Has notes" />
                     )}
                   </Box>
                ) : (
                  <MinusIcon color="gray.200" w={2} h={2} />
                )}
              </Box>
            </ScorecardCellEditor>
          </Td>
        );
      })}
      
      {/* Trailing Status Column */}
      <Td 
        textAlign="center" 
        position="sticky" 
        right={0} 
        bg="white" 
        zIndex={1} 
        boxShadow="-2px 0 5px -2px rgba(0,0,0,0.1)"
      >
        <Box display="flex" justifyContent="center" alignItems="center">
            {(() => {
                // Calculate trailing status based on the weeks
                const weeklyStatuses = weeks.map(week => {
                    const goal = getGoalForWeek(week.weekNumber);
                     // If no data for a past week, it's considered missed in EOS strict mode, 
                     // but usually we check if actualValue is present. 
                     // Here we treat null as 'no-data' which calculateTrailingStatus handles.
                    const actualValue = goal?.actualValue ?? null;
                    const targetGoal = goal?.goalValue ?? (metric.weeklyGoal || metric.quarterlyGoal || 0);
                    
                    if (actualValue === null) return null;
                    
                    return isMetricOnTrack(
                        actualValue,
                        targetGoal,
                        metric.goalOrientation || 'gte',
                        metric.goalRangeMin,
                        metric.goalRangeMax
                    ) ?? null;
                });

                const status = calculateTrailingStatus(weeklyStatuses, 13); // Look at all 13 weeks or just 3? Workflow says "Based on the last 3 weeks with data"

                /* 
                   Correction: The workflow says "Based on the last 3 weeks with data". 
                   calculateTrailingStatus helper defaults to lookbackWeeks=3.
                */
               const trailingStatus = calculateTrailingStatus(weeklyStatuses, 3);

                if (trailingStatus === 'green') return <Text fontSize="xl">🟢</Text>;
                if (trailingStatus === 'yellow') return <Text fontSize="xl">🟡</Text>;
                if (trailingStatus === 'red') return <Text fontSize="xl">🔴</Text>;
                return <Text fontSize="sm" color="gray.400">-</Text>;
            })()}
        </Box>
      </Td>
    </Tr>
  );
}
